import { AfterViewInit, Component, ElementRef, ViewChild, OnInit, OnDestroy, NgZone, Output, EventEmitter } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

/* Dynamsoft */
import { initializeDynamsoft } from '@app/dynamsoft.config';
import { BarcodeFormat } from '@src/app/shared/enums/BarcodeFormat.enum';
import { CameraEnhancer, CameraView, Feedback, CaptureVisionRouter, MultiFrameResultCrossFilter } from 'dynamsoft-barcode-reader-bundle';

import { ScannerCameraStatus } from '@app/shared/enums/ScannerCameraStatus.enum';
const ERROR_DESTROYED = 'videoCapture component destroyed';

@Component({
    selector: 'app-single-scanner',
    templateUrl: './single-scanner.component.html',
    styleUrls: ['./single-scanner.component.scss'],
    standalone: false
})
export class SingleScannerComponent implements OnInit, AfterViewInit, OnDestroy {

  @Output() qrCodeScanned = new EventEmitter<string>();
  @Output() scannerClosed = new EventEmitter<void>();
  @Output() scannerError = new EventEmitter<any>();

  @ViewChild('uiWrapper') uiWrapper?: ElementRef<HTMLDivElement>;

  private resolveInit?: () => void;
  private pInit: Promise<void> = new Promise(resolve => {
    this.resolveInit = resolve;
  });

  private bDestroyed = false;
  private isCapturing = false;
  private pendingCameraOperation: Promise<void> = Promise.resolve();
  private hasAcceptedScan = false;

  QRCodeValue: string = '';
  barcodeFormat = BarcodeFormat;
  cvRouter?: CaptureVisionRouter;
  cameraEnhancer?: CameraEnhancer;
  cameraView?: CameraView;
  cameraVersion = '';
  camerasList: any[] = [];
  selectedCameraId: string = '';
  hasFlash: boolean = false;
  isFlashOn: boolean = false;
  cameraStatus = ScannerCameraStatus.IDLE;

  constructor(
    public zone: NgZone,
    private translate: TranslateService
  ) {
  }

  get isCameraOpen(): boolean {
    return this.cameraStatus === ScannerCameraStatus.OPEN;
  }

  get showCameraState(): boolean {
    return !this.QRCodeValue && this.cameraStatus !== ScannerCameraStatus.OPEN;
  }

  get showCameraStartGuide(): boolean {
    return this.cameraStatus === ScannerCameraStatus.IDLE
      || this.cameraStatus === ScannerCameraStatus.READY
      || this.cameraStatus === ScannerCameraStatus.PAUSED;
  }

  get showCameraLoader(): boolean {
    return this.cameraStatus === ScannerCameraStatus.INITIALIZING
      || this.cameraStatus === ScannerCameraStatus.OPENING;
  }

  get showCameraActionButton(): boolean {
    return this.cameraStatus === ScannerCameraStatus.CAMERA_BUSY
      || this.cameraStatus === ScannerCameraStatus.ERROR;
  }

  get showDismissCameraMessage(): boolean {
    return this.cameraStatus === ScannerCameraStatus.PERMISSION_DENIED
      || this.cameraStatus === ScannerCameraStatus.CAMERA_NOT_FOUND
      || this.cameraStatus === ScannerCameraStatus.CAMERA_BUSY
      || this.cameraStatus === ScannerCameraStatus.ERROR;
  }

  get cameraActionButtonLabel(): string {
    if (this.cameraStatus === ScannerCameraStatus.CAMERA_BUSY) {
      return this.translate.instant('CAMERA_SCANNER.BTN_TRY_AGAIN');
    }

    return this.translate.instant('CAMERA_SCANNER.BTN_OPEN_CAMERA');
  }

  get cameraStatusTitle(): string {
    switch (this.cameraStatus) {
      case ScannerCameraStatus.INITIALIZING:
        return this.translate.instant('CAMERA_SCANNER.PREPARING_CAMERA');

      case ScannerCameraStatus.OPENING:
        return this.translate.instant('CAMERA_SCANNER.OPENING_CAMERA');

      case ScannerCameraStatus.PERMISSION_DENIED:
        return this.translate.instant('CAMERA_SCANNER.CAMERA_PERMISSION_BLOCKED');

      case ScannerCameraStatus.CAMERA_NOT_FOUND:
        return this.translate.instant('CAMERA_SCANNER.NO_CAMERA_FOUND');

      case ScannerCameraStatus.CAMERA_BUSY:
        return this.translate.instant('CAMERA_SCANNER.CAMERA_UNAVAILABLE');

      case ScannerCameraStatus.ERROR:
        return this.translate.instant('CAMERA_SCANNER.CAMERA_COULD_NOT_START');

      default:
        return '';
    }
  }

  get cameraStatusDescription(): string {
    switch (this.cameraStatus) {
      case ScannerCameraStatus.OPENING:
        return this.translate.instant('CAMERA_SCANNER.ALLOW_CAMERA_ACCESS');

      case ScannerCameraStatus.PERMISSION_DENIED:
        return this.translate.instant('CAMERA_SCANNER.CAMERA_ACCESS_BLOCKED_DESCRIPTION');

      case ScannerCameraStatus.CAMERA_NOT_FOUND:
        return this.translate.instant('CAMERA_SCANNER.NO_CAMERA_FOUND_DESCRIPTION');

      case ScannerCameraStatus.CAMERA_BUSY:
        return this.translate.instant('CAMERA_SCANNER.CAMERA_BUSY_DESCRIPTION');

      case ScannerCameraStatus.ERROR:
        return this.translate.instant('CAMERA_SCANNER.CAMERA_ERROR_DESCRIPTION');

      default:
        return '';
    }
  }

  ngOnInit() {
    // console.log('DynamSoft scanner version', CaptureVisionRouterModule.getVersion());
  }

  async ngAfterViewInit(): Promise<void> {
    this.setCameraStatus(ScannerCameraStatus.INITIALIZING);

    try {
      await this.initializeScanner();

      this.throwIfDestroyed();

      this.setCameraStatus(ScannerCameraStatus.READY);
    } catch (error: any) {
      this.handleError(error);
    } finally {
      this.resolveInit?.();
    }
  }

  ngOnDestroy(): void {
    this.bDestroyed = true;
    this.cleanupResources();
  }

  async toggleFlash(): Promise<void> {
    if (!this.cameraEnhancer || !this.cameraEnhancer.isOpen()) return;

    const capabilities: any = this.cameraEnhancer.getCapabilities();

    if (!capabilities?.torch) return;

    try {
      if (this.isFlashOn) {
        await this.cameraEnhancer.turnOffTorch();
      } else {
        await this.cameraEnhancer.turnOnTorch();
      }

      this.isFlashOn = !this.isFlashOn;
    } catch (error: any) {
      console.warn('Error toggling scanner flash', error);
      this.isFlashOn = false;
    }
  }

  async toggleCamera(): Promise<void> {
    this.QRCodeValue = '';

    if (this.cameraEnhancer?.isOpen()) {
      await this.closeCamera(ScannerCameraStatus.PAUSED);
    } else {
      await this.openCamera();
    }
  }

  private async openCameraInternal(): Promise<void> {
    if (!this.cameraEnhancer || this.bDestroyed) return;

    if (this.cameraEnhancer.isOpen()) {
      this.setCameraStatus(ScannerCameraStatus.OPEN);
      return;
    }

    const permissionState = await this.getCameraPermissionState();

    if (this.bDestroyed) {
      return;
    }

    if (permissionState === 'denied') {
      const error = new Error('Camera permission is blocked. Check your browser permissions and allow camera access.');

      this.setCameraStatus(ScannerCameraStatus.PERMISSION_DENIED);
      this.scannerError.emit(error);

      return;
    }

    this.setCameraStatus(ScannerCameraStatus.OPENING);

    try {
      await this.cameraEnhancer.open();

      if (this.bDestroyed) {
        return;
      }

      await this.startCapturing();

      if (this.bDestroyed) {
        return;
      }

      this.setCameraStatus(ScannerCameraStatus.OPEN);
    } catch (error: any) {
      if (this.bDestroyed) {
        return;
      }

      this.applyCameraOpenError(error);
    }
  }

  private async closeCameraInternal(nextStatus = ScannerCameraStatus.PAUSED): Promise<void> {
    try {
      await this.stopCapturing();

      if (this.isFlashOn && this.cameraEnhancer?.isOpen()) {
        await this.cameraEnhancer.turnOffTorch();
      }

      if (this.cameraEnhancer?.isOpen()) {
        this.cameraEnhancer.close();
      }

      this.releaseBrowserCameraStream();
    } catch (error: any) {
      console.warn('Error closing scanner camera', error);
    } finally {
      this.isFlashOn = false;
      this.hasFlash = false;

      if (!this.bDestroyed) {
        this.setCameraStatus(nextStatus);
      }
    }
  }

  async getAllCameras(): Promise<any> {
    const camerasList = await this.cameraEnhancer?.getAllCameras();

    if (camerasList?.length) {
      this.camerasList = camerasList;
      this.selectedCameraId = this.selectedCameraId || camerasList[0]?.deviceId || '';
    }

    return camerasList;
  }

  async onCameraChange(event: Event): Promise<void> {
    const cameraId = (event.target as HTMLSelectElement).value;

    if (!cameraId || !this.cameraEnhancer) return;

    this.selectedCameraId = cameraId;

    try {
      await this.cameraEnhancer.selectCamera(cameraId);
    } catch (error: any) {
      this.handleError(error);
    }
  }

  public async closeScanner(): Promise<void> {
    await this.closeCamera();
  }

  dismissCameraMessage(): void {
    this.QRCodeValue = '';
    this.setCameraStatus(ScannerCameraStatus.READY);
  }

  private setCameraStatus(status: ScannerCameraStatus): void {
    this.cameraStatus = status;
  }

  private async initializeScanner(): Promise<void> {
    await initializeDynamsoft();
    this.throwIfDestroyed();

    this.cameraView = await CameraView.createInstance();
    this.throwIfDestroyed();

    this.cameraEnhancer = await CameraEnhancer.createInstance(this.cameraView);
    this.setupCameraEvents();

    const cameraUI = this.cameraView.getUIElement();

    cameraUI.querySelector('.dce-sel-camera')?.remove();
    cameraUI.querySelector('.dce-sel-resolution')?.remove();

    this.cameraView.setPowerByMessageVisible(false);

    // populate cameras dropdown
    await this.getAllCameras();

    this.throwIfDestroyed();

    if (!this.uiWrapper?.nativeElement) {
      throw new Error('Scanner UI wrapper was not found.');
    }

    // append SDK camera UI from the installed package
    this.uiWrapper.nativeElement.append(this.cameraView.getUIElement());

    this.cvRouter = await CaptureVisionRouter.createInstance();
    await this.setupRouterSettings();

    this.throwIfDestroyed();

    // retrieve image stream
    this.cvRouter.setInput(this.cameraEnhancer);

    // process results object
    await this.cvRouter.addResultReceiver({
      onDecodedBarcodesReceived: capturedData => {
        this.handleScannerResults(capturedData);
      }
    });

    await this.setupResultFilters();

    this.throwIfDestroyed();

    this.cameraView.setScanLaserVisible(false);
  }

  private setupCameraEvents(): void {
    this.cameraEnhancer?.on('cameraOpen', () => {
      this.zone.run(() => {
        const deviceCapabilities: any = this.cameraEnhancer?.getCapabilities();

        if (deviceCapabilities?.torch) {
          this.hasFlash = true;
        } else {
          this.hasFlash = false;
        }

        this.setCameraStatus(ScannerCameraStatus.OPEN);
      });
    });

    this.cameraEnhancer?.on('cameraClose', () => {
      this.zone.run(() => {
        this.QRCodeValue = '';
        this.isFlashOn = false;
        this.hasFlash = false;

        if (!this.bDestroyed && this.cameraStatus === ScannerCameraStatus.OPEN) {
          this.setCameraStatus(ScannerCameraStatus.PAUSED);
        }
      });
    });
  }

  private async setupRouterSettings(): Promise<void> {
    if (!this.cvRouter) return;

    const settings = await this.cvRouter.getSimplifiedSettings('ReadSingleBarcode');

    settings.barcodeSettings.barcodeFormatIds = BigInt(this.barcodeFormat.BF_QR_CODE);

    await this.cvRouter.updateSettings('ReadSingleBarcode', settings);
  }

  private async setupResultFilters(): Promise<void> {
    if (!this.cvRouter) return;

    // Verify results across multiple frames
    const filter = new MultiFrameResultCrossFilter();

    filter.setDuplicateForgetTime('barcode', 5000);
    filter.enableResultCrossVerification('barcode', true);
    filter.enableResultDeduplication('barcode', true);

    await this.cvRouter.addResultFilter(filter);
  }

  private async startCapturing(): Promise<void> {
    if (!this.cvRouter || this.isCapturing) return;

    this.hasAcceptedScan = false;

    await this.cvRouter.startCapturing('ReadSingleBarcode');

    this.cameraView?.setScanLaserVisible(false);
    this.isCapturing = true;
  }

  private async stopCapturing(): Promise<void> {
    if (!this.cvRouter || !this.isCapturing) return;

    await this.cvRouter.stopCapturing();
    this.isCapturing = false;
  }

  private queueCameraOperation(operation: () => Promise<void>): Promise<void> {
    // allow next operation to run even if previous failed
    const nextOperation = this.pendingCameraOperation.then(
      operation,
      operation
    );

    this.pendingCameraOperation = nextOperation;

    return nextOperation;
  }

  private async handleScannerResults(capturedData: any): Promise<void> {
    if (
      this.bDestroyed ||
      this.hasAcceptedScan ||
      !capturedData.barcodeResultItems.length
    ) {
      return;
    }

    this.hasAcceptedScan = true;

    try {
      Feedback.beep();
    } catch (error) {
      console.warn('Scanner beep feedback failed', error);
    }

    await this.closeCamera();

    if (this.bDestroyed) {
      return;
    }

    // reset value before new scans
    let qrValue = '';

    capturedData.barcodeResultItems.forEach((item: any) => {
      // console.log(`**text**: ${item.text}`);
      qrValue = item.text;
    });

    this.zone.run(() => {
      this.QRCodeValue = qrValue;
      this.qrCodeScanned.emit(qrValue);
      this.scannerClosed.emit();
    });
  }

  private async getCameraPermissionState(): Promise<string> {
    if (!navigator.permissions?.query) {
      return 'unsupported';
    }

    try {
      const permissionStatus = await navigator.permissions.query({
        name: 'camera' as any
      });

      return permissionStatus.state;
    } catch (_) {
      return 'unsupported';
    }
  }

  private applyCameraOpenError(error: any): void {
    const errorName = String(error?.name ?? '').toLowerCase();
    const errorMessage = String(error?.message ?? error ?? '').toLowerCase();
    const errorText = `${errorName} ${errorMessage}`;

    if (
      errorText.includes('notallowed') ||
      errorText.includes('permission') ||
      errorText.includes('denied')
    ) {
      this.setCameraStatus(ScannerCameraStatus.PERMISSION_DENIED);
      this.scannerError.emit(error);
      return;
    }

    if (
      errorText.includes('notfound') ||
      errorText.includes('device not found') ||
      errorText.includes('no camera')
    ) {
      this.setCameraStatus(ScannerCameraStatus.CAMERA_NOT_FOUND);
      this.scannerError.emit(error);
      return;
    }

    if (
      errorText.includes('notreadable') ||
      errorText.includes('could not start') ||
      errorText.includes('in use')
    ) {
      this.setCameraStatus(ScannerCameraStatus.CAMERA_BUSY);
      this.scannerError.emit(error);
      return;
    }

    this.setCameraStatus(ScannerCameraStatus.ERROR);
    this.scannerError.emit(error);
  }

  openCamera(): Promise<void> {
    return this.queueCameraOperation(() => {
      return this.openCameraInternal();
    });
  }

  closeCamera(nextStatus = ScannerCameraStatus.PAUSED): Promise<void> {
    return this.queueCameraOperation(() => {
      return this.closeCameraInternal(nextStatus);
    });
  }

  private releaseBrowserCameraStream(): void {
    // Fallback cleanup for the browser MediaStream when the SDK keeps the video alive.
    const hostElement = this.uiWrapper?.nativeElement;
    const videos = Array.from(hostElement?.querySelectorAll('video') ?? []);

    videos.forEach(video => {
      const stream = video.srcObject as MediaStream | null;

      stream?.getTracks().forEach(track => {
        track.stop();
      });

      video.pause();
      video.srcObject = null;
      video.removeAttribute('src');
      video.load();
    });
  }

  private throwIfDestroyed(): void {
    if (this.bDestroyed) {
      throw new Error(ERROR_DESTROYED);
    }
  }

  private async cleanupResources(): Promise<void> {
    try {
      await this.pInit;

      await this.closeCamera(ScannerCameraStatus.IDLE);

      this.cvRouter?.dispose();
      this.cameraEnhancer?.dispose();
    } catch (error: any) {
      if (error?.message === ERROR_DESTROYED) return;

      console.warn('Error cleaning up scanner resources', error);
    }
  }

  private handleError(error: any): void {
    if (error?.message === ERROR_DESTROYED) {
      console.log(ERROR_DESTROYED);
      return;
    }

    this.setCameraStatus(ScannerCameraStatus.ERROR);
    console.error(error?.message || error);

    this.scannerError.emit(error);
  }
}
